/**
 * PurgeCancelledButton (09/2026)
 * Löscht stornierte + No-Show-Termine ENDGÜLTIG aus der DB.
 * - Ohne customerId: global (alle Kunden).
 * - Mit customerId: nur dieser Kunde.
 * Zeigt vor dem Löschen die Anzahl und eine klare Warnung.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function PurgeCancelledButton({
  customerId = null,
  customerName = null,
  label,
  variant = 'outline',
  size,
  iconOnly = false,
  className = '',
  onDone,
  'data-testid': testId = 'purge-cancelled-btn',
}) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const qs = customerId ? `?customer_id=${customerId}` : '';
  const scopeLabel = customerId
    ? (customerName ? `von ${customerName}` : 'dieses Kunden')
    : 'ALLER Kunden';

  const openDialog = async () => {
    setOpen(true);
    setCount(null);
    setLoadingCount(true);
    try {
      const res = await axios.get(`${API}/appointments/purge-removed/count${qs}`, auth());
      setCount(res.data?.count ?? 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Anzahl konnte nicht geladen werden');
      setCount(0);
    } finally {
      setLoadingCount(false);
    }
  };

  const doPurge = async () => {
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/appointments/purge-removed${qs}`, auth());
      const n = res.data?.deleted ?? 0;
      toast.success(`${n} stornierte/No-Show-Termine endgültig gelöscht`);
      setOpen(false);
      onDone?.(n);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size={size || (iconOnly ? 'icon' : undefined)}
        onClick={openDialog}
        className={`gap-2 ${iconOnly ? 'h-8 w-8' : ''} ${className}`}
        data-testid={testId}
        title={iconOnly ? (label || 'Stornierte Termine entfernen') : undefined}
      >
        <Trash2 className="h-4 w-4" />
        {!iconOnly && (label || 'Stornierte Termine entfernen')}
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="purge-cancelled-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" />
              Stornierte Termine endgültig löschen?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-gray-600">
                <p>
                  Es werden alle <strong>stornierten</strong> und <strong>nicht erschienenen (No-Show)</strong>
                  {' '}Termine {scopeLabel} <strong>unwiderruflich</strong> aus der Datenbank gelöscht
                  – auch vergangene. Aktive/geplante Termine bleiben unberührt.
                </p>
                <div className="bg-rose-50 border border-rose-200 rounded p-3" data-testid="purge-count">
                  {loadingCount ? (
                    <span className="flex items-center gap-2 text-gray-700">
                      <Loader2 className="h-4 w-4 animate-spin" /> Anzahl wird geladen…
                    </span>
                  ) : (
                    <span className="text-rose-800 font-medium">
                      {count} Termin{count === 1 ? '' : 'e'} werden gelöscht
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500">Diese Aktion kann nicht rückgängig gemacht werden.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="purge-cancel-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doPurge(); }}
              disabled={deleting || loadingCount || !count}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="purge-confirm-btn"
            >
              {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Löschen…</> : 'Endgültig löschen'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
